/**
 * Almacena el access_token entre páginas/sesiones. Implementaciones
 * intercambiables:
 *
 * - `LocalStorageTokenStorage` para web (MVP)
 * - Tauri Stronghold cuando se active desktop
 * - Keychain/Keystore para nativo móvil
 *
 * El token expira y el backend NO documenta refresh endpoint
 * (`02_CONTRATOS.md`). Al expirar, las requests autenticadas devuelven
 * 401 y `@alter/api-client` lanza `UnauthorizedError`. El caller decide
 * cuándo limpiar este storage.
 */
export interface TokenStorage {
  /** Lee el token actual. `null` si no hay sesión. */
  get(): Promise<string | null>;
  /** Guarda un token (tras login exitoso). */
  set(token: string): Promise<void>;
  /** Limpia el token (tras logout o 401 detectado por el caller). */
  clear(): Promise<void>;
}

/** Clave por defecto usada en `Storage`. Configurable por el constructor. */
export const TOKEN_STORAGE_KEY = 'alter_access_token';

/**
 * Implementación basada en `Storage` del navegador (típicamente
 * `localStorage`). Se inyecta por constructor; el paquete no asume
 * acceso a `globalThis.localStorage`.
 *
 * **Limitación de seguridad.** `localStorage` es accesible vía JS y por
 * tanto vulnerable a XSS. Aceptable en MVP web; reemplazar por Stronghold
 * (Tauri) o Keychain (móvil nativo) cuando esos clientes se activen.
 */
export class LocalStorageTokenStorage implements TokenStorage {
  constructor(
    private readonly storage: Storage,
    private readonly key: string = TOKEN_STORAGE_KEY,
  ) {}

  async get(): Promise<string | null> {
    return this.storage.getItem(this.key);
  }

  async set(token: string): Promise<void> {
    this.storage.setItem(this.key, token);
  }

  async clear(): Promise<void> {
    this.storage.removeItem(this.key);
  }
}
